import { useEffect, useState } from 'react';
import { styled } from '@mui/material';
import { useLazyQuery, useMutation } from '@apollo/client';
import { graphql } from '../../../gql';
import Typography from '@mui/material/Typography';
import { useAuth } from '@calefy-inc/authentication';
import LoadingScreen from '../../ManagementPanel/components/LoadingScreen';
import { DisplayInlineError } from '../../common/ErrorHandling';
import { isNotNullish } from '../../../util';
import { PolicystreamInsuranceCompaniesQuery } from '../../../gql/graphql';
import { ArrayElement } from '@calefy-inc/utilityTypes';
import { UpdateInsuranceCompanyProductsButton } from './UpdateInsuranceCompanyProductsButton';
import Accordion from '@mui/material/Accordion';
import AccordionSummary from '@mui/material/AccordionSummary';
import AccordionDetails from '@mui/material/AccordionDetails';
import Checkbox from '@mui/material/Checkbox';
import FormControlLabel from '@mui/material/FormControlLabel';

export const PolicyStreamInsuranceCompanies = () => {
  const { token } = useAuth();
  const [getInsurers, { loading, error, data }] = useLazyQuery(
    POLICYSTREAM_INSURANCE_COMPANIES,
  );

  useEffect(() => {
    if (token) {
      getInsurers({ variables: { token } });
    }
  }, [token]);

  return (
    <PolicyStreamInsuranceCompaniesContainer>
      <Typography variant='h1'>Insurance Companies</Typography>
      {loading ? <LoadingScreen /> : null}
      <DisplayInlineError error={error} />
      {data &&
      data.policystreamInsuranceCompanies &&
      data.policystreamInsuranceCompanies.length === 0 ? (
        <Typography>No PolicyStream Insurance Companies Found</Typography>
      ) : null}
      {data &&
      data.policystreamInsuranceCompanies &&
      data.policystreamInsuranceCompanies?.length > 0
        ? data.policystreamInsuranceCompanies
            .filter(isNotNullish)
            .sort((a, b) => a.displayName.localeCompare(b.displayName))
            .map((company) => (
              <DisplayInsuranceCompany key={company.id} company={company} />
            ))
        : null}
    </PolicyStreamInsuranceCompaniesContainer>
  );
};

interface DisplayInsuranceCompanyProps {
  company: BackendInsuranceCompany;
}
const DisplayInsuranceCompany = ({ company }: DisplayInsuranceCompanyProps) => {
  return (
    <Accordion>
      <AccordionSummary>{company.displayName}</AccordionSummary>
      <AccordionDetails>
        <ul>
          <li>Name: {company.displayName}</li>
          <li>API name: {company.apiName}</li>
          <li>
            Logo:{' '}
            {company.logoUrl ? (
              <img height={LOGO_HEIGHT} src={company.logoUrl} />
            ) : (
              'None'
            )}
          </li>
          <li>
            Logo URL:{' '}
            {company.logoUrl ? (
              <a href={company.logoUrl}>{company.logoUrl}</a>
            ) : null}
          </li>
          {(company.products || []).length > 0 ? (
            <li>
              Products:{' '}
              {(company.products || []).map((product) => (
                <DisplayProduct product={product} />
              ))}
            </li>
          ) : null}
          <li>
            <UpdateInsuranceCompanyProductsButton company={company} />
          </li>
        </ul>
      </AccordionDetails>
    </Accordion>
  );
};

export interface BackendInsuranceCompany {
  id: string;
  displayName: string;
  apiName: string;
  logoUrl?: string | null;
  products: Array<BackendInsuranceCompanyProduct>;
}
export interface BackendInsuranceCompanyProduct {
  id: string;
  included: boolean;
  name: string;
  categoryLetter: string;
  prodCode: string;
}

interface PolicyStreamInsuranceCompanyRowProps {
  company: ArrayElement<
    NonNullable<
      PolicystreamInsuranceCompaniesQuery['policystreamInsuranceCompanies']
    >
  >;
}
// @ts-expect-error
const PolicyStreamInsuranceCompanyRow = ({
  company,
}: PolicyStreamInsuranceCompanyRowProps) => {
  return (
    <tr>
      <td>{company?.displayName}</td>
      <td>{company.apiName}</td>
      <td>
        {company.logoUrl ? (
          <img height={LOGO_HEIGHT} src={company.logoUrl} />
        ) : null}
      </td>
      <td>
        {company.logoUrl ? (
          <a href={company.logoUrl}>{company.logoUrl}</a>
        ) : null}
      </td>
      <td>
        {
          // @ts-expect-error
          (company.products || []).map((product) => (
            <DisplayProduct product={product} />
          ))
        }
      </td>
      <td>
        <UpdateInsuranceCompanyProductsButton company={company} />
      </td>
    </tr>
  );
};

interface DisplayProductProps {
  product: BackendInsuranceCompanyProduct;
}
const DisplayProduct = ({ product }: DisplayProductProps) => {
  const [included, setIncluded] = useState<boolean>(product.included);
  const { token } = useAuth();
  // @ts-expect-error
  const [updateProduct, { loading, error, data }] = useMutation(
    UPDATE_POLICYSTREAM_INSURANCE_COMPANY_PRODUCT,
    {
      refetchQueries: [
        { query: POLICYSTREAM_INSURANCE_COMPANIES, variables: { token } },
      ],
    },
  );

  useEffect(() => {
    if (token && included !== product.included) {
      updateProduct({
        variables: {
          token,
          productId: product.id,
          included,
        },
      });
    }
  }, [included, token]);

  return (
    <ProductContainer>
      <FormControlLabel
        control={
          <Checkbox
            checked={included}
            onChange={(e) => setIncluded(e.target.checked)}
          />
        }
        label={`${product.name}(${product.prodCode}-${product.categoryLetter})`}
      />
    </ProductContainer>
  );
};

const ProductContainer = styled('div')({});

export const PolicyStreamInsuranceCompaniesContainer = styled('div')(
  ({ theme }) => {
    return {
      padding: theme.spacing(1),
    };
  },
);

// @ts-expect-error
const PolicyStreamInsuranceCompaniesTable = styled('table')(() => {
  return {
    '& td': {
      textAlign: 'center',
    },
  };
});

const LOGO_HEIGHT = '50px';

export const POLICYSTREAM_INSURANCE_COMPANIES = graphql(`
  query policystreamInsuranceCompanies($token: String!) {
    policystreamInsuranceCompanies(token: $token) {
      id
      displayName
      apiName
      logoUrl
      products {
        id
        included
        name
        categoryLetter
        prodCode
      }
    }
  }
`);

const UPDATE_POLICYSTREAM_INSURANCE_COMPANY_PRODUCT = graphql(`
  mutation updatePolicystreamInsuranceCompanyProduct(
    $token: String!
    $productId: ID!
    $included: Boolean
  ) {
    updatePolicystreamInsuranceCompanyProduct(
      token: $token
      productId: $productId
      included: $included
    ) {
      product {
        id
        included
        name
        categoryLetter
        prodCode
      }
    }
  }
`);
